import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Sparkles, Check, XCircle, Zap } from 'lucide-react';
import { processMessage, getGreeting, getStepMessage, getErrorTip, getFieldTip } from './agentController';
import { ActionType, getFieldLabel, getSportLabel } from './agentActions';

/**
 * OnboardingAssistant — Agent-powered chat widget for registration & wizard.
 *
 * Architecture:
 *   User types → agentController.processMessage() → returns messages + actions
 *   Actions rendered as cards with ✅ Aplicar / ❌ Ignorar
 *   On "Aplicar" → onApplyAction callback fires → parent updates form state
 *
 * Props:
 *   context        — 'register' | 'wizard'
 *   step           — Current step number
 *   focusedField   — Currently focused form field name
 *   errors         — Current validation errors { fieldName: message }
 *   formData       — Current form state { fieldName: value }
 *   onApplyAction  — (action) => void — Callback to apply agent actions to the form
 *   onNavigateStep — (step) => void — Callback to change wizard step
 */

const V = {
  bg: '#111111',
  botBubble: '#ffffff',
  botText: '#111111',
  userBubble: '#2a2a2a',
  userText: '#ffffff',
  success: '#16a34a',
  mute: '#8a8a8a',
  border: '#e5e7eb',
  actionBg: '#f0fdf4',
  actionBorder: '#bbf7d0',
  pageBg: '#fafafa',
};

const OnboardingAssistant = ({
  context = 'register',
  step = 1,
  focusedField = null,
  errors = {},
  formData = {},
  onApplyAction = () => {},
  onNavigateStep = () => {},
}) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [lastFieldTip, setLastFieldTip] = useState(null);
  const [lastErrorTip, setLastErrorTip] = useState(null);
  const [lastStepTip, setLastStepTip] = useState(null);
  const [appliedActions, setAppliedActions] = useState(new Set());
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const actionIdCounter = useRef(0);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Focus input on open
  useEffect(() => {
    if (open && inputRef.current) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  // Greeting
  useEffect(() => {
    if (open && !initialized) {
      const greetings = getGreeting(context).map((text) => ({ from: 'bot', text, ts: Date.now() }));
      setMessages(greetings);
      setInitialized(true);
    }
  }, [open, initialized, context]);

  // Field tips
  useEffect(() => {
    if (!open || !focusedField || focusedField === lastFieldTip) return;
    const tip = getFieldTip(focusedField);
    if (tip) { addBot(tip); setLastFieldTip(focusedField); }
  }, [open, focusedField, lastFieldTip]);

  // Error tips
  useEffect(() => {
    if (!open) return;
    const firstErr = Object.keys(errors).find((k) => errors[k]);
    if (firstErr && firstErr !== lastErrorTip) { addBot(getErrorTip(firstErr)); setLastErrorTip(firstErr); }
  }, [open, errors, lastErrorTip]);

  // Step tips (wizard)
  useEffect(() => {
    if (!open || context !== 'wizard' || step === lastStepTip) return;
    const msg = getStepMessage(step);
    if (msg) addBot(msg);
    setLastStepTip(step);
  }, [open, context, step, lastStepTip]);

  /* ── Helpers ── */
  const addBot = (text, actions = null) => {
    setMessages((prev) => [...prev, { from: 'bot', text, ts: Date.now(), actions }]);
  };
  const nextId = () => { actionIdCounter.current += 1; return `act-${actionIdCounter.current}`; };

  /* ── Send message → controller ── */
  const dispatchToAgent = useCallback((text) => {
    const response = processMessage({ message: text, context, step, formData, errors });
    for (const m of response.messages) addBot(m);
    const confirmable = response.actions.filter(
      (a) => a.type !== ActionType.SUMMARIZE && a.type !== ActionType.SHOW_PENDING
    );
    if (confirmable.length > 0) {
      const group = confirmable.map((a) => ({ ...a, id: nextId() }));
      setMessages((prev) => [...prev, { from: 'bot', text: null, ts: Date.now(), actions: group }]);
    }
  }, [context, step, formData, errors]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setMessages((prev) => [...prev, { from: 'user', text, ts: Date.now() }]);
    setInput('');
    setTimeout(() => dispatchToAgent(text), 300);
  }, [input, dispatchToAgent]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  /* ── Apply / Dismiss ── */
  const handleApply = useCallback((action) => {
    setAppliedActions((prev) => new Set([...prev, action.id]));
    if (action.type === ActionType.NAVIGATE_STEP) {
      onNavigateStep(action.step);
      addBot(`Navegando para etapa ${action.step}.`);
    } else {
      onApplyAction(action);
      addBot('Aplicado! Confira os campos atualizados.');
    }
  }, [onApplyAction, onNavigateStep]);

  const handleDismiss = useCallback((action) => {
    setAppliedActions((prev) => new Set([...prev, action.id]));
    addBot('Ignorado.');
  }, []);

  /* ── Progress ── */
  const getProgress = () => {
    if (context === 'register') {
      const req = ['full_name', 'email', 'password', 'confirmPassword', 'primary_sport'];
      const done = req.filter((k) => formData[k] && String(formData[k]).trim().length > 0);
      return `${done.length}/${req.length} campos`;
    }
    return `Etapa ${step}/7`;
  };

  /* ── Quick actions ── */
  const quickActions = context === 'register'
    ? [
        { label: 'Resumo', msg: 'resumo' },
        { label: 'Pendências', msg: 'o que falta' },
        { label: 'Preço?', msg: 'quanto custa' },
      ]
    : [
        { label: 'Resumo', msg: 'resumo' },
        { label: 'Pendências', msg: 'o que falta' },
        { label: 'Módulos', msg: 'quais módulos recomenda' },
        ...(step >= 4 && step <= 6 ? [{ label: 'Pular', msg: 'pular etapa' }] : []),
      ];

  /* ══════════════════════════════════════════════════════════
     ACTION CARD
     ══════════════════════════════════════════════════════════ */
  const renderActionCard = (action) => {
    const done = appliedActions.has(action.id);
    if (done) {
      return (
        <div key={action.id} className="px-3 py-2 rounded-lg text-xs opacity-50"
          style={{ backgroundColor: '#f3f4f6', color: V.mute }}>
          {action.confirmLabel || 'Ação aplicada'}
        </div>
      );
    }
    return (
      <div key={action.id} className="rounded-xl border-2 overflow-hidden"
        style={{ borderColor: V.actionBorder, backgroundColor: V.actionBg }}>
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Zap size={12} style={{ color: V.success }} />
            <span className="text-xs font-bold" style={{ color: V.success }}>AÇÃO SUGERIDA</span>
          </div>

          {action.type === ActionType.FILL_FIELDS && action.fields && (
            <div className="space-y-1">
              {Object.entries(action.fields).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-xs">
                  <span style={{ color: V.mute }}>{getFieldLabel(k)}:</span>
                  <span className="font-semibold" style={{ color: V.botText }}>
                    {k === 'primary_sport' || k === 'sport_name' ? getSportLabel(v) : String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {action.type === ActionType.SELECT_SPORT && (
            <div className="text-xs">
              <span style={{ color: V.mute }}>Esporte: </span>
              <span className="font-semibold" style={{ color: V.botText }}>{getSportLabel(action.sportId)}</span>
            </div>
          )}

          {action.type === ActionType.SELECT_MODULES && (
            <div className="text-xs">
              <span style={{ color: V.mute }}>{action.moduleCodes.length} módulos recomendados</span>
            </div>
          )}

          {action.type === ActionType.SEARCH_CLUB && (
            <div className="text-xs">
              <span style={{ color: V.mute }}>Buscar: </span>
              <span className="font-semibold">"{action.query}"</span>
            </div>
          )}

          {action.type === ActionType.NAVIGATE_STEP && (
            <div className="text-xs"><span style={{ color: V.mute }}>Ir para etapa {action.step}</span></div>
          )}

          {action.type === ActionType.ADD_SPORT_ENTRY && (
            <div className="space-y-1 text-xs">
              {action.sportData.sport_name && (
                <div><span style={{ color: V.mute }}>Esporte:</span> <span className="font-semibold">{getSportLabel(action.sportData.sport_name)}</span></div>
              )}
              {action.sportData.num_athletes && (
                <div><span style={{ color: V.mute }}>Atletas:</span> <span className="font-semibold">{action.sportData.num_athletes}</span></div>
              )}
              {action.sportData.num_coaches && (
                <div><span style={{ color: V.mute }}>Treinadores:</span> <span className="font-semibold">{action.sportData.num_coaches}</span></div>
              )}
            </div>
          )}

          {action.type === ActionType.ADD_INVITE && action.inviteData && (
            <div className="space-y-1 text-xs">
              <div><span style={{ color: V.mute }}>Email:</span> <span className="font-semibold">{action.inviteData.invite_email}</span></div>
              <div><span style={{ color: V.mute }}>Papel:</span> <span className="font-semibold">{action.inviteData.invite_role}</span></div>
            </div>
          )}

          {action.reason && <p className="text-xs mt-1" style={{ color: V.mute }}>{action.reason}</p>}
        </div>

        <div className="flex border-t" style={{ borderColor: V.actionBorder }}>
          <button onClick={() => handleApply(action)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold transition hover:opacity-80"
            style={{ color: V.success }}>
            <Check size={13} /> Aplicar
          </button>
          <div className="w-px" style={{ backgroundColor: V.actionBorder }} />
          <button onClick={() => handleDismiss(action)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-bold transition hover:opacity-80"
            style={{ color: V.mute }}>
            <XCircle size={13} /> Ignorar
          </button>
        </div>
      </div>
    );
  };

  /* ══════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════ */
  return (
    <>
      {/* Floating bubble */}
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110 active:scale-95"
          style={{ backgroundColor: V.bg }}
          aria-label="Abrir agente de onboarding">
          <Sparkles size={24} className="text-white" />
          <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white animate-pulse"
            style={{ backgroundColor: V.success }} />
        </button>
      )}

      {/* Agent panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[560px] rounded-2xl shadow-2xl flex flex-col overflow-hidden border"
          style={{ backgroundColor: V.pageBg, borderColor: V.border }}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: V.bg }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Sparkles size={16} className="text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">Agente VUKZ</p>
                <p className="text-white/60 text-xs">{getProgress()}</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition">
              <X size={14} className="text-white" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-[240px] max-h-[340px]">
            {messages.map((msg, i) => (
              <div key={i}>
                {msg.text && (
                  <div className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'} mb-1`}>
                    <div className={`max-w-[88%] px-3.5 py-2.5 rounded-2xl text-[13px] leading-relaxed whitespace-pre-line ${
                      msg.from === 'user' ? 'rounded-br-md' : 'rounded-bl-md'
                    }`}
                    style={{
                      backgroundColor: msg.from === 'user' ? V.userBubble : V.botBubble,
                      color: msg.from === 'user' ? V.userText : V.botText,
                      border: msg.from === 'bot' ? `1px solid ${V.border}` : 'none',
                    }}>
                      {msg.text}
                    </div>
                  </div>
                )}
                {msg.actions && msg.actions.length > 0 && (
                  <div className="space-y-2 ml-1">
                    {msg.actions.map((a) => renderActionCard(a))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Quick actions */}
          <div className="px-3 pt-1 pb-1 flex gap-1.5 overflow-x-auto">
            {quickActions.map((qa) => (
              <button key={qa.msg}
                onClick={() => {
                  setMessages((prev) => [...prev, { from: 'user', text: qa.msg, ts: Date.now() }]);
                  setTimeout(() => dispatchToAgent(qa.msg), 300);
                }}
                className="flex-shrink-0 px-2.5 py-1.5 rounded-full text-xs font-medium border transition hover:shadow-sm"
                style={{ borderColor: V.border, color: V.botText, backgroundColor: '#ffffff' }}>
                {qa.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <div className="px-3 pb-3 pt-1.5">
            <div className="flex items-center gap-2 rounded-xl border px-3 py-2.5"
              style={{ borderColor: V.border, backgroundColor: '#ffffff' }}>
              <input ref={inputRef} type="text" value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Descreva seu clube ou pergunte..."
                className="flex-1 text-sm bg-transparent outline-none placeholder-gray-400"
                style={{ color: V.botText }} />
              <button onClick={handleSend} disabled={!input.trim()}
                className="w-8 h-8 rounded-lg flex items-center justify-center transition-opacity disabled:opacity-30"
                style={{ backgroundColor: V.bg }}>
                <Send size={14} className="text-white" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardingAssistant;
